import React, { useState, useCallback, useMemo } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import {
  TemplateBodyAutocomplete,
  substituteTemplateVariables,
  detectTemplateVariables,
  convertNamedVariablesToNumbered,
} from '../../ui/template-body-autocomplete';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';

export interface CreateTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  creating: boolean;
  onSubmit: (data: {
    name: string;
    language: string;
    category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
    bodyText: string;
    headerText?: string;
    headerType?: 'TEXT';
    footerText?: string;
    bodyExamples?: string[];
    headerExample?: string;
  }) => Promise<{ success: boolean; error?: string }>;
  onSaveVariableMapping: (
    templateName: string,
    variableMapping: string[]
  ) => Promise<{ success: boolean; error?: string }>;
}

export function CreateTemplateDialog({
  open,
  onOpenChange,
  creating,
  onSubmit,
  onSaveVariableMapping,
}: CreateTemplateDialogProps) {
  // Form state
  const [templateName, setTemplateName] = useState('');
  const [templateCategory, setTemplateCategory] = useState<
    'MARKETING' | 'UTILITY' | 'AUTHENTICATION'
  >('MARKETING');
  const [templateLanguage, setTemplateLanguage] = useState('pt_BR');
  const [templateBodyText, setTemplateBodyText] = useState('');
  const [templateHeaderEnabled, setTemplateHeaderEnabled] = useState(false);
  const [templateHeaderText, setTemplateHeaderText] = useState('');
  const [templateFooterEnabled, setTemplateFooterEnabled] = useState(false);
  const [templateFooterText, setTemplateFooterText] = useState('');
  const [templateHeaderExample, setTemplateHeaderExample] = useState('');
  const [createTemplateError, setCreateTemplateError] = useState<string | null>(null);

  // Extract named variables from body text
  const detectedVariables = useMemo(() => {
    return detectTemplateVariables(templateBodyText);
  }, [templateBodyText]);

  // Extract header placeholder
  const headerHasPlaceholder = useMemo(() => {
    return templateHeaderEnabled && /\{\{1\}\}/.test(templateHeaderText);
  }, [templateHeaderEnabled, templateHeaderText]);

  // Generate preview text with named variables substituted
  const previewBodyText = useMemo(() => {
    return substituteTemplateVariables(templateBodyText);
  }, [templateBodyText]);

  const previewHeaderText = useMemo(() => {
    if (!templateHeaderEnabled || !templateHeaderText) return '';
    if (headerHasPlaceholder) {
      return templateHeaderText.replace(/\{\{1\}\}/, templateHeaderExample || '[Exemplo Cabecalho]');
    }
    return templateHeaderText;
  }, [templateHeaderEnabled, templateHeaderText, headerHasPlaceholder, templateHeaderExample]);

  // Reset form
  const resetForm = useCallback(() => {
    setTemplateName('');
    setTemplateCategory('MARKETING');
    setTemplateLanguage('pt_BR');
    setTemplateBodyText('');
    setTemplateHeaderEnabled(false);
    setTemplateHeaderText('');
    setTemplateFooterEnabled(false);
    setTemplateFooterText('');
    setTemplateHeaderExample('');
    setCreateTemplateError(null);
  }, []);

  // Auto-format template name
  const handleTemplateNameChange = useCallback((value: string) => {
    const formatted = value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    setTemplateName(formatted);
  }, []);

  // Handle create template submission
  const handleSubmit = useCallback(async () => {
    if (!templateName.trim()) {
      setCreateTemplateError('Nome do template e obrigatorio');
      return;
    }
    if (!templateBodyText.trim()) {
      setCreateTemplateError('Corpo da mensagem e obrigatorio');
      return;
    }

    setCreateTemplateError(null);

    // Convert named variables to numbered placeholders
    const { convertedText, bodyExamples, variableNames } =
      convertNamedVariablesToNumbered(templateBodyText.trim());

    const data = {
      name: templateName.trim(),
      language: templateLanguage,
      category: templateCategory,
      bodyText: convertedText,
      ...(templateHeaderEnabled &&
        templateHeaderText.trim() && {
          headerText: templateHeaderText.trim(),
          headerType: 'TEXT' as const,
        }),
      ...(templateFooterEnabled &&
        templateFooterText.trim() && {
          footerText: templateFooterText.trim(),
        }),
      ...(bodyExamples.length > 0 && {
        bodyExamples,
      }),
      ...(headerHasPlaceholder &&
        templateHeaderExample.trim() && {
          headerExample: templateHeaderExample.trim(),
        }),
    };

    const result = await onSubmit(data);

    if (result.success) {
      // Auto-save the variable mapping if there are any variables
      if (variableNames.length > 0) {
        onSaveVariableMapping(templateName.trim(), variableNames).catch((err) => {
          console.warn('Failed to save template variable mapping:', err);
        });
      }

      onOpenChange(false);
      resetForm();
    } else {
      setCreateTemplateError(result.error || 'Erro ao criar template');
    }
  }, [
    templateName,
    templateLanguage,
    templateCategory,
    templateBodyText,
    templateHeaderEnabled,
    templateHeaderText,
    templateFooterEnabled,
    templateFooterText,
    headerHasPlaceholder,
    templateHeaderExample,
    onSubmit,
    onOpenChange,
    resetForm,
    onSaveVariableMapping,
  ]);

  // Reset form when dialog closes
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        resetForm();
      }
      onOpenChange(newOpen);
    },
    [onOpenChange, resetForm]
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Criar Novo Template</DialogTitle>
          <DialogDescription>
            Crie um novo template de mensagem para uso em campanhas WhatsApp. O template sera
            enviado para aprovacao do Meta.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Left Side - Form */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="template-name">Nome do Template *</Label>
              <Input
                id="template-name"
                value={templateName}
                onChange={(e) => handleTemplateNameChange(e.target.value)}
                placeholder="meu_template_exemplo"
              />
              <p className="text-xs text-muted-foreground">
                Somente letras minusculas, numeros e underscores.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="template-category">Categoria *</Label>
              <select
                id="template-category"
                value={templateCategory}
                onChange={(e) =>
                  setTemplateCategory(e.target.value as 'MARKETING' | 'UTILITY' | 'AUTHENTICATION')
                }
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="MARKETING">Marketing</option>
                <option value="UTILITY">Utility</option>
                <option value="AUTHENTICATION">Authentication</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="template-language">Idioma *</Label>
              <select
                id="template-language"
                value={templateLanguage}
                onChange={(e) => setTemplateLanguage(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="pt_BR">Portugues (Brasil)</option>
                <option value="en_US">English (US)</option>
                <option value="es">Espanol</option>
              </select>
            </div>

            <div className="flex items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <Label htmlFor="template-header-toggle">Cabecalho</Label>
                <p className="text-xs text-muted-foreground">
                  Adicionar texto de cabecalho (opcional)
                </p>
              </div>
              <Switch
                id="template-header-toggle"
                checked={templateHeaderEnabled}
                onCheckedChange={setTemplateHeaderEnabled}
              />
            </div>

            {templateHeaderEnabled && (
              <div className="space-y-2">
                <Label htmlFor="template-header">Texto do Cabecalho</Label>
                <Input
                  id="template-header"
                  value={templateHeaderText}
                  onChange={(e) => setTemplateHeaderText(e.target.value)}
                  placeholder="Titulo ou cabecalho da mensagem"
                />
                <p className="text-xs text-muted-foreground">
                  Use {'{{1}}'} para incluir uma variavel dinamica no cabecalho.
                </p>
              </div>
            )}

            {headerHasPlaceholder && (
              <div className="space-y-2">
                <Label htmlFor="template-header-example">Exemplo do Cabecalho</Label>
                <Input
                  id="template-header-example"
                  value={templateHeaderExample}
                  onChange={(e) => setTemplateHeaderExample(e.target.value)}
                  placeholder="Ex: Joao Silva"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="template-body">Corpo da Mensagem *</Label>
              <TemplateBodyAutocomplete
                id="template-body"
                value={templateBodyText}
                onChange={setTemplateBodyText}
                placeholder="Ola {{Nome}}, sua empresa {{Empresa}} esta confirmada!"
                minHeight="120px"
              />
              <p className="text-xs text-muted-foreground">
                Digite {'{{ '} para inserir variaveis dinamicas.
              </p>
            </div>

            <div className="flex items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <Label htmlFor="template-footer-toggle">Rodape</Label>
                <p className="text-xs text-muted-foreground">
                  Adicionar texto de rodape (opcional)
                </p>
              </div>
              <Switch
                id="template-footer-toggle"
                checked={templateFooterEnabled}
                onCheckedChange={setTemplateFooterEnabled}
              />
            </div>

            {templateFooterEnabled && (
              <div className="space-y-2">
                <Label htmlFor="template-footer">Texto do Rodape</Label>
                <Input
                  id="template-footer"
                  value={templateFooterText}
                  onChange={(e) => setTemplateFooterText(e.target.value)}
                  placeholder="Ex: Responda SAIR para cancelar"
                />
              </div>
            )}
          </div>

          {/* Right Side - Preview */}
          <div className="space-y-4">
            <Label className="text-sm font-medium">Preview da Mensagem</Label>
            <div className="rounded-lg border bg-gray-100 dark:bg-gray-900 p-4 min-h-[300px]">
              <div className="max-w-[280px] mx-auto">
                <div className="rounded-lg bg-green-100 dark:bg-green-900/50 p-3 shadow-sm relative">
                  <div className="absolute -left-2 top-0 w-0 h-0 border-t-[10px] border-t-green-100 dark:border-t-green-900/50 border-l-[10px] border-l-transparent" />

                  {templateHeaderEnabled && previewHeaderText && (
                    <div className="font-semibold text-sm text-gray-900 dark:text-gray-100 mb-2 pb-2 border-b border-green-200 dark:border-green-800">
                      {previewHeaderText}
                    </div>
                  )}

                  <div className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                    {previewBodyText || (
                      <span className="text-gray-400 dark:text-gray-500 italic">
                        O preview aparecera aqui...
                      </span>
                    )}
                  </div>

                  {templateFooterEnabled && templateFooterText && (
                    <div className="mt-2 pt-2 border-t border-green-200 dark:border-green-800 text-xs text-gray-500 dark:text-gray-400">
                      {templateFooterText}
                    </div>
                  )}

                  <div className="text-right mt-1">
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">
                      {new Date().toLocaleTimeString('pt-BR', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-lg border p-3 space-y-2">
              <Label className="text-sm font-medium">Resumo do Template</Label>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-muted-foreground">Nome:</span>
                  <span className="ml-1 font-medium">{templateName || '-'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Categoria:</span>
                  <span className="ml-1 font-medium">{templateCategory}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Idioma:</span>
                  <span className="ml-1 font-medium">
                    {templateLanguage === 'pt_BR'
                      ? 'Portugues'
                      : templateLanguage === 'en_US'
                        ? 'English'
                        : 'Espanol'}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Variaveis:</span>
                  <span className="ml-1 font-medium">{detectedVariables.length}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {createTemplateError && (
          <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-3">
            <p className="text-sm text-red-600 dark:text-red-400">{createTemplateError}</p>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={creating}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={creating || !templateName.trim() || !templateBodyText.trim()}
          >
            {creating ? 'Criando...' : 'Criar Template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
